<template>
  <div class="container">
    <h1>Carga masiva de alojamientos por Excel</h1>
    <p>Aquí podrás subir un archivo CSV (.csv) para crear o actualizar alojamientos en masa. Descarga la plantilla, complétala y súbela aquí.</p>
    <div class="upload-row">
      <input type="file" @change="handleFileUpload" accept=".csv,text/csv,application/vnd.ms-excel" />
      <button @click="downloadTemplate" class="template-btn">Descargar plantilla</button>
    </div>
    <h2 style="margin-top: 24px;">Búsqueda de usuario</h2>
    <form @submit.prevent="buscarUsuario" class="user-search-form">
      <input v-model="busqueda" type="text" placeholder="ID de usuario o email" class="form-control" style="max-width:300px;display:inline-block;margin-right:8px;" />
      <button type="button" @click="buscarUsuario" class="template-btn">Buscar</button>
    </form>

    <h2 style="margin-top: 24px;">Dirección (Google)</h2>
    <p class="mb-2">Escribe una dirección y selecciona una opción para ver su latitud y longitud.</p>
    <div class="address-form">
      <div v-if="isGoogleMapsLoaded">
        <vue-google-autocomplete
          id="bulk_address_autocomplete"
          class="form-control"
          placeholder="Ej: Av. Providencia 1234, Santiago"
          v-model="direccionSeleccionada"
          v-on:placechanged="handleAddressSelected"
          v-on:error="handleGoogleAutocompleteError"
        ></vue-google-autocomplete>
      </div>
      <div v-else>
        <p>Cargando Google Maps...</p>
      </div>

      <div v-if="latitudSeleccionada !== null && longitudSeleccionada !== null" class="mt-3">
        <div class="row">
          <div class="col-md-6">
            <label class="small mb-1">Latitud</label>
            <input class="form-control" type="text" :value="latitudSeleccionada" readonly />
          </div>
          <div class="col-md-6">
            <label class="small mb-1">Longitud</label>
            <input class="form-control" type="text" :value="longitudSeleccionada" readonly />
          </div>
        </div>
      </div>
    </div>
    <div v-if="error && !uploading && !success" class="mt-2 text-danger">{{ error }}</div>
    <div v-if="usuarioEncontrado" class="mt-3 user-data-box">
      <h3>Datos del usuario</h3>
      <div style="margin-bottom:8px;">
        <button @click="exportUserAccommodations" class="template-btn" :disabled="exporting">
          {{ exporting ? 'Exportando...' : 'Exportar' }}
        </button>
      </div>
      <table class="table table-bordered table-sm">
        <tbody>
          <tr v-for="(valor, clave) in usuarioEncontrado" :key="clave">
            <th>{{ clave }}</th>
            <td>{{ valor }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="uploading" class="mt-3">Subiendo archivo...</div>
    <div v-if="error" class="mt-3 text-danger">{{ error }}</div>
    <div v-if="success" class="mt-3 text-success">Carga exitosa.</div>
    <div v-if="resultados && (resultados.exitosos.length || resultados.errores.length)" class="mt-3">
      <div v-if="resultados.exitosos.length">
        <b>Alojamientos procesados:</b>
        <ul>
          <li v-for="(item, idx) in resultados.exitosos" :key="'ok'+idx">
            {{ item }}
          </li>
        </ul>
      </div>
      <div v-if="resultados.errores.length">
        <b>Errores:</b>
        <ul>
          <li v-for="(err, idx) in resultados.errores" :key="'err'+idx">{{ err }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import apiUrls from '@/new-constants/apiUrls';
import VueGoogleAutocomplete from 'vue-google-autocomplete';
export default {
  name: "AccommodationCharges",
  components: {
    VueGoogleAutocomplete,
  },
  data() {
    return {
      uploading: false,
      error: '',
      success: false,
      resultados: { exitosos: [], errores: [] },
      busqueda: '',
      usuarioEncontrado: null,
        exporting: false,
      isGoogleMapsLoaded: false,
      direccionSeleccionada: '',
      latitudSeleccionada: null,
      longitudSeleccionada: null,
    };
  },
  methods: {
    async buscarUsuario() {
      this.usuarioEncontrado = null;
      this.error = '';
      if (!this.busqueda) {
        this.error = 'Ingresa un ID de usuario o email.';
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${process.env.VUE_APP_API_URL}admin/find-user`, {
          params: { query: this.busqueda },
          headers: { 'Authorization': token ? token : '' }
        });
        if (resp.data && resp.data.usuario) {
          this.usuarioEncontrado = resp.data.usuario;
        } else {
          this.error = 'Usuario no encontrado.';
        }
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) ? e.response.data.message : 'Error al buscar usuario.';
      }
    },

    async exportUserAccommodations() {
      if (!this.usuarioEncontrado || !this.usuarioEncontrado.idusuario) return;
      this.exporting = true;
      this.error = '';
      try {
        const token = localStorage.getItem('token');
        const url = `${apiUrls.adminExportAccommodationsByUser}${this.usuarioEncontrado.idusuario}`;
        const resp = await axios.get(url, { headers: { 'Authorization': token ? token : '' } });
        const data = Array.isArray(resp.data) ? resp.data : (resp.data.data || []);

        if (!data || data.length === 0) {
          this.error = 'No se encontraron alojamientos para exportar.';
          return;
        }

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Alojamientos');
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `alojamientos_usuario_${this.usuarioEncontrado.idusuario}.xlsx`);
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) ? e.response.data.message : (e.message || 'Error al exportar.');
      } finally {
        this.exporting = false;
      }
    },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel'
      ];
      const fileName = file.name.toLowerCase();
      const isValidType = allowedTypes.includes(file.type) || fileName.endsWith('.csv');
      if (!isValidType) {
        this.error = 'Solo se permiten archivos .csv';
        return;
      }
      this.uploading = true;
      this.error = '';
      this.success = false;
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      try {
        const resp = await axios.post(
          `${process.env.VUE_APP_API_URL}accommodations/bulk-upload-excel`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': token ? token : ''
            }
          }
        );
        this.success = true;
        this.resultados = {
          exitosos: resp.data.exitosos || [],
          errores: resp.data.errores || []
        };
      } catch (e) {
        if (e.response && e.response.data) {
          this.error = e.response.data.message;
          this.resultados = {
            exitosos: e.response.data.exitosos || [],
            errores: e.response.data.errores || []
          };
        } else if (e.message) {
          this.error = e.message;
        } else {
          this.error = 'Error al subir el archivo.';
        }
      } finally {
        this.uploading = false;
      }
    },
    downloadTemplate() {
      window.open('/plantillas/ejemplo_masivo.csv', '_blank');
    },

    handleAddressSelected(addressData, placeResultData) {
      if (addressData && typeof addressData.latitude !== 'undefined' && typeof addressData.longitude !== 'undefined') {
        this.latitudSeleccionada = addressData.latitude;
        this.longitudSeleccionada = addressData.longitude;
      }

      if (placeResultData && placeResultData.formatted_address) {
        this.direccionSeleccionada = placeResultData.formatted_address;
      }
    },

    handleGoogleAutocompleteError() {
      // Mantenerlo silencioso como en otras pantallas; si hace falta, lo levantamos con this.error
    },
  },

  mounted() {
    if (window.googleMapsLoaded && typeof window.googleMapsLoaded.then === 'function') {
      window.googleMapsLoaded
        .then(() => {
          this.isGoogleMapsLoaded = true;
        })
        .catch(() => {
          this.isGoogleMapsLoaded = false;
        });
    } else {
      // Fallback por si la promesa no existe (igual la API puede estar cargada por vue2-google-maps)
      this.isGoogleMapsLoaded = typeof window.google !== 'undefined';
    }
  }
};
</script>

<style scoped>
.user-search-form {
  margin-bottom: 16px;
}
.user-data-box {
  background: #f7f7f7;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
.container {
  max-width: 600px;
  margin: 40px auto;
  background: #fff;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
}
h1 {
  color: #003D29;
  margin-bottom: 24px;
}
.upload-row {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
}
.template-btn {
  background: #003D29;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
}

.address-form {
  margin-bottom: 16px;
}
</style>
